import { useState } from "react";
import { toast } from "sonner";
import { Phone, KeyRound, Lock, CalendarCheck, Send, CheckCircle2 } from "lucide-react";
import { useLang, useL } from "@/lib/i18n";
import { api, errDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function CallButton({ contact, id = "arrange-keycard-btn" }) {
  const L = useL();
  const tel = contact?.phone_tel || "+15144310520";
  const shown = contact?.phone_display || "514-431-0520";
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href={`tel:${tel}`} data-testid={id} className="btn-red inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold">
        <Phone size={16} />{L("Appeler pour organiser la remise", "Call to Arrange Keycard Pickup")}
      </a>
      <span className="font-mono text-lg text-white" data-testid={`${id}-number`}>{shown}</span>
    </div>
  );
}

export const KEY_MSG = {
  fr: "Le paiement de votre abonnement est confirmé. Appelez-nous pour organiser votre rencontre d’accueil et récupérer votre carte d’accès. Apportez une pièce d’identité valide avec photo. La réservation du studio en ligne sera disponible dès que nous aurons confirmé la remise de votre carte.",
  en: "Your membership payment is confirmed. Call us to arrange your introductory meeting and keycard pickup. Bring a valid photo ID. Online studio booking will become available as soon as we confirm that you have received your keycard.",
};

function RequestPickup({ request, onChange }) {
  const { tErr } = useLang();
  const L = useL();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const sentText = L("Demande envoyée. Nous vous contacterons pour organiser votre rencontre.", "Request sent. We will contact you to arrange your meeting.");
  if (request) return <p className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-3 text-sm text-emerald-200" data-testid="keycard-request-sent"><CheckCircle2 size={16} className="mt-0.5 shrink-0" />{sentText}</p>;
  const send = async () => {
    setBusy(true);
    try { await api.post("/member/keycard-request", { message: msg }); toast.success(sentText); await onChange?.(); }
    catch (e) { toast.error(tErr(errDetail(e))); setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="keycard-request-form">
      <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={1000} data-testid="keycard-request-message" rows={3} className="min-h-[96px] border-white/15 bg-black/40"
        placeholder={L("Optionnel : vos disponibilités pour la rencontre (ex. mardi soir, samedi matin)", "Optional: your preferred meeting times (e.g. Tuesday evening, Saturday morning)")} />
      <button disabled={busy} onClick={send} data-testid="keycard-request-btn" className="btn-chrome inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-50">
        <Send size={16} />{busy ? "…" : L("Demander la remise de ma carte", "Request Keycard Pickup")}
      </button>
    </div>
  );
}

export function KeycardPending({ contact, request, onChange }) {
  const { lang } = useLang();
  const L = useL();
  return (
    <div className="panel brackets mb-10 border-amber-500/40 p-5 sm:p-6" data-testid="keycard-pending-panel">
      <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-3 py-1 font-mono text-xs uppercase tracking-widest text-amber-300" data-testid="keycard-pending-badge">
        <KeyRound size={14} />{L("Remise de carte en attente", "Keycard pickup pending")}
      </p>
      <p className="text-base text-slate-200" data-testid="keycard-pending-msg">{KEY_MSG[lang] || KEY_MSG.en}</p>
      <p className="mt-3 text-sm text-amber-200" data-testid="keycard-booking-explain">{L("La réservation en ligne exige que le studio confirme la remise de votre carte. Elle s’ouvrira ici automatiquement.", "Online booking requires the studio to confirm your keycard handover. It will open here automatically.")}</p>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div><p className="mb-2 text-xs uppercase tracking-widest text-slate-500">{L("Par téléphone", "By phone")}</p><CallButton contact={contact} /></div>
        {onChange && <div><p className="mb-2 text-xs uppercase tracking-widest text-slate-500">{L("Par le site", "Through the website")}</p><RequestPickup request={request} onChange={onChange} /></div>}
      </div>
      <p className="mt-4 text-xs text-slate-500">{L("Ces options servent uniquement à organiser la rencontre d’accueil et la remise de carte ; une demande n’est pas un rendez-vous confirmé. Les séances d’enregistrement se réservent en ligne, ici. Aucune heure n’est déduite.",
        "These options are only to arrange the introductory meeting and keycard pickup; a request is not a confirmed appointment. Recording sessions are booked online, here. No hours are used.")}</p>
    </div>
  );
}

const REASONS = {
  payment_pending: ["Paiement en cours de confirmation", "Waiting for payment confirmation",
    "Nous attendons la confirmation de votre paiement. Cette page se met à jour automatiquement.", "We’re waiting for your payment to be confirmed. This page updates automatically."],
  keycard_pending: ["Réservation verrouillée : remise de carte en attente", "Booking locked: keycard handover pending",
    "La réservation en ligne s’ouvrira automatiquement dès que le studio aura confirmé la remise de votre carte d’accès.", "Online booking opens automatically as soon as the studio confirms your keycard handover."],
  membership_inactive: ["Abonnement inactif ou expiré", "Membership inactive or expired",
    "Un abonnement payé et actif est requis pour réserver. Choisissez ou renouvelez un forfait.", "An active paid membership is required to book. Choose or renew a plan."],
  insufficient_hours: ["Heures insuffisantes", "Not enough hours",
    "Votre solde d’heures est inférieur à la durée minimale d’une séance. Achetez des heures supplémentaires ou attendez votre renouvellement.", "Your hour balance is below the minimum session length. Buy extra hours or wait for your renewal."],
};

export function BookingLocked({ state, contact }) {
  const L = useL();
  const r = REASONS[state] || REASONS.membership_inactive;
  return (
    <div className="panel p-6" data-testid={`booking-locked-${state}`}>
      <p className="flex items-center gap-2 font-semibold text-amber-200"><Lock size={16} />{L(r[0], r[1])}</p>
      <p className="mt-2 text-sm text-slate-300">{L(r[2], r[3])}</p>
      {state === "keycard_pending" && <div className="mt-4"><CallButton contact={contact} id="locked-arrange-keycard-btn" /></div>}
      {state === "insufficient_hours" && <a href="#extra-hours" className="btn-chrome mt-4 inline-block rounded-full px-4 py-2 text-sm" data-testid="locked-buy-hours">{L("Acheter des heures", "Buy hours")}</a>}
    </div>
  );
}

export function BookCta({ onClick }) {
  const L = useL();
  return (
    <div className="panel brackets mb-10 flex flex-wrap items-center justify-between gap-4 border-emerald-500/40 p-5" data-testid="booking-unlocked-banner">
      <p className="flex items-center gap-2 text-emerald-300"><CalendarCheck size={18} />{L("Votre carte est confirmée : la réservation en ligne est ouverte.", "Your keycard is confirmed: online booking is open.")}</p>
      <button onClick={onClick} data-testid="book-studio-session-btn" className="btn-red rounded-full px-5 py-2.5 text-sm font-semibold">{L("Réserver une séance au studio", "Book a Studio Session")}</button>
    </div>
  );
}

function PField({ id, label, ...p }) {
  return <div className="space-y-1.5"><Label htmlFor={id} className="text-xs text-slate-400">{label}</Label><Input id={id} data-testid={id} className="border-white/15 bg-black/40" {...p} /></div>;
}

export function ProfileCard({ user, onSaved }) {
  const { tErr } = useLang();
  const L = useL();
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "" });
  const [em, setEm] = useState({ new_email: "", current_password: "" });
  const [pending, setPending] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(tErr(errDetail(e))); } finally { setBusy(false); } };
  const save = (e) => { e.preventDefault(); run(async () => {
    if (!f.name.trim()) throw Object.assign(new Error(), { response: { data: { detail: "name_required" } } });
    await api.put("/member/profile", f); toast.success(L("Modifications enregistrées.", "Changes saved.")); onSaved();
  }); };
  const changeEmail = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/member/email-change", em); setPending(data.pending_email); setEm({ new_email: "", current_password: "" });
    toast.success(L("Lien de confirmation envoyé à la nouvelle adresse.", "Confirmation link sent to the new address."));
  }); };
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="account-tab">
      <form onSubmit={save} className="panel space-y-4 p-5 sm:p-6" data-testid="profile-form">
        <h3 className="font-display text-2xl uppercase text-white">{L("Mes informations", "My information")}</h3>
        <PField id="profile-name-input" label={L("Nom complet", "Full name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
        <PField id="profile-phone-input" label={L("Téléphone", "Phone")} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required />
        <p className="text-sm text-slate-400">{L("Courriel de connexion", "Sign-in email")}: <span className="text-white" data-testid="profile-current-email">{user.email}</span></p>
        <button disabled={busy} data-testid="profile-save-btn" className="btn-red h-11 rounded-full px-6 font-semibold disabled:opacity-60">{L("Enregistrer les modifications", "Save Changes")}</button>
      </form>
      <form onSubmit={changeEmail} className="panel space-y-4 p-5 sm:p-6" data-testid="email-change-form">
        <h3 className="font-display text-2xl uppercase text-white">{L("Changer de courriel", "Change email")}</h3>
        <p className="text-xs text-slate-500">{L("Pour votre sécurité, confirmez votre mot de passe. Un lien sera envoyé à la nouvelle adresse ; votre adresse actuelle reste active jusqu’à la confirmation.", "For your security, confirm your password. A link is sent to the new address; your current address stays active until confirmed.")}</p>
        <PField id="email-change-new-input" label={L("Nouveau courriel", "New email")} type="email" required value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} />
        <PField id="email-change-password-input" label={L("Mot de passe actuel", "Current password")} type="password" required value={em.current_password} onChange={(e) => setEm({ ...em, current_password: e.target.value })} />
        {pending && <p className="text-sm text-emerald-300" data-testid="email-change-pending">{L(`En attente de confirmation : ${pending}`, `Awaiting confirmation: ${pending}`)}</p>}
        <button disabled={busy} data-testid="email-change-submit-btn" className="btn-chrome h-11 rounded-full px-6 font-semibold disabled:opacity-60">{L("Envoyer le lien de confirmation", "Send confirmation link")}</button>
      </form>
    </div>
  );
}
